import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, GoogleLogo, Barbell } from '@phosphor-icons/react';
import { useAuth, formatApiErrorDetail } from '@/contexts/AuthContext';
import { LOGIN } from '@/constants/testIds';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth() {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const from = location.state?.from || '/account';

    async function onSubmit(e) {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        try {
            await login(email, password);
            navigate(from, { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <main className="mx-auto max-w-md px-5 sm:px-8 pt-16 pb-32">
            <div className="glass-card rounded-3xl p-7 sm:p-9 animate-fade-up">
                <div className="flex items-center gap-3 mb-6">
                    <span className="grid place-items-center w-10 h-10 rounded-2xl bg-foreground/5 border border-foreground/10">
                        <Barbell size={20} weight="bold" />
                    </span>
                    <div>
                        <p className="uppercase-mono text-muted-foreground">
                            Welcome back
                        </p>
                        <h1 className="font-display font-extrabold tracking-tighter text-2xl">
                            Sign in to GymFlow
                        </h1>
                    </div>
                </div>

                <button
                    type="button"
                    onClick={startGoogleAuth}
                    data-testid="login-google-button"
                    className="w-full h-12 rounded-2xl border border-foreground/15 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.99] transition-all inline-flex items-center justify-center gap-3 font-semibold"
                >
                    <GoogleLogo size={20} weight="bold" />
                    Continue with Google
                </button>

                <div className="my-6 flex items-center gap-3">
                    <span className="flex-1 h-px bg-foreground/10" />
                    <span className="uppercase-mono text-muted-foreground">
                        or email
                    </span>
                    <span className="flex-1 h-px bg-foreground/10" />
                </div>

                <form onSubmit={onSubmit} className="space-y-4">
                    <Field
                        label="Email"
                        type="email"
                        autoComplete="email"
                        testId={LOGIN.emailInput}
                        value={email}
                        onChange={setEmail}
                        required
                    />
                    <Field
                        label="Password"
                        type="password"
                        autoComplete="current-password"
                        testId={LOGIN.passwordInput}
                        value={password}
                        onChange={setPassword}
                        required
                    />

                    {error && (
                        <p
                            data-testid="login-error"
                            className="text-sm text-[#FF4D4D] font-medium"
                        >
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        data-testid={LOGIN.submitButton}
                        disabled={submitting}
                        className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-bold tracking-tight shadow-neon hover:-translate-y-[2px] active:scale-[0.99] transition-all disabled:opacity-60 disabled:hover:translate-y-0 inline-flex items-center justify-center gap-2"
                    >
                        {submitting ? 'Signing in…' : 'Sign in'}
                        {!submitting && <ArrowRight size={18} weight="bold" />}
                    </button>
                </form>

                <p className="mt-6 text-sm text-muted-foreground text-center">
                    New here?{' '}
                    <Link
                        to="/register"
                        data-testid={LOGIN.registerLink}
                        className="text-foreground font-semibold hover:underline underline-offset-4"
                    >
                        Create an account
                    </Link>
                </p>
            </div>
        </main>
    );
}

function Field({ label, type, value, onChange, testId, autoComplete, required }) {
    return (
        <label className="block">
            <span className="uppercase-mono text-muted-foreground block mb-2">
                {label}
            </span>
            <input
                data-testid={testId}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                autoComplete={autoComplete}
                required={required}
                className="w-full h-12 rounded-2xl border border-foreground/15 bg-foreground/5 px-4 outline-none focus:border-foreground/40 transition-colors placeholder:text-muted-foreground/70"
            />
        </label>
    );
}
